/**
 * @fileoverview SubscribeButton Component
 * @description Button to subscribe/unsubscribe from event notifications.
 *              Shows subscriber count and current subscription state.
 * @module features/TestCalendar/components/SubscribeButton
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Tooltip, Chip, Box,
} from '@mui/material';
import Spinner from '@/components/common/Spinner/Spinner';
import { BellRing, BellOff, Users } from 'lucide-react';
import { calendarApi } from '@/services/api/calendar';
import { useAuth } from '@/context';

/**
 * Subscribe/Unsubscribe button with subscriber count
 * @param {Object} props
 * @param {number} props.eventId - Event ID to subscribe to
 * @param {Function} props.onSubscriptionChange - Callback after subscribe/unsubscribe
 */
export const SubscribeButton = ({ eventId, onSubscriptionChange }) => {
  const { getUserProfile } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const user = getUserProfile();
  const userId = user?.email || user?.id || '';
  const userName = user?.name || '';

  /**
   * Check current subscription status
   */
  const checkStatus = useCallback(async () => {
    if (!eventId || !userId) return;
    setLoading(true);
    try {
      const response = await calendarApi.checkSubscription(eventId, userId);
      const data = response?.data ?? response;
      setIsSubscribed(data?.is_subscribed ?? false);
      setSubscriberCount(data?.subscriber_count ?? 0);
    } catch (err) {
      console.error('Failed to check subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, userId]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  /**
   * Toggle subscription
   */
  const handleToggle = async () => {
    if (!userId || actionLoading) return;
    setActionLoading(true);
    try {
      if (isSubscribed) {
        await calendarApi.unsubscribe(eventId, userId);
        setIsSubscribed(false);
        setSubscriberCount((prev) => Math.max(0, prev - 1));
      } else {
        await calendarApi.subscribe(eventId, userId, userName);
        setIsSubscribed(true);
        setSubscriberCount((prev) => prev + 1);
      }
      onSubscriptionChange?.();
    } catch (err) {
      console.error('Failed to toggle subscription:', err);
      // Re-check state on error
      checkStatus();
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Spinner size={16} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip
        title={isSubscribed
          ? 'Unsubscribe — You will no longer receive notifications for this event'
          : 'Subscribe — Get notified about changes to this event'
        }
        arrow
      >
        <Button
          size="small"
          variant={isSubscribed ? 'contained' : 'outlined'}
          startIcon={
            actionLoading
              ? <Spinner size={14} color="inherit" />
              : isSubscribed
                ? <BellRing size={16} />
                : <BellOff size={16} />
          }
          onClick={handleToggle}
          disabled={actionLoading}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            minWidth: 'auto',
            ...(isSubscribed
              ? {
                  backgroundColor: '#0097A9',
                  '&:hover': { backgroundColor: '#007A8A' },
                }
              : {
                  borderColor: '#D1D5DB',
                  color: '#4B5563',
                  '&:hover': {
                    borderColor: '#0097A9',
                    color: '#0097A9',
                    backgroundColor: 'rgba(0, 151, 169, 0.04)',
                  },
                }),
          }}
        >
          {isSubscribed ? 'Subscribed' : 'Subscribe'}
        </Button>
      </Tooltip>

      {subscriberCount > 0 && (
        <Tooltip title={`${subscriberCount} subscriber${subscriberCount !== 1 ? 's' : ''}`} arrow>
          <Chip
            icon={<Users size={13} />}
            label={subscriberCount}
            size="small"
            variant="outlined"
            sx={{
              height: 26,
              fontSize: '0.75rem',
              fontWeight: 600,
              borderColor: '#E5E7EB',
              color: '#6B7280',
              '& .MuiChip-icon': { color: '#9CA3AF' },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default SubscribeButton;
